/**
 * №16.1 Stage C — the workflow template roster (the maker's list). Console tree,
 * D-WF14 (authoring lives in the console, never the App Shell). Rows link to the
 * author canvas; a Workflows/Blocks toggle splits the two template kinds.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import "../components/workflow-author/workflow-author.css";
import { showToast } from "../components/primitives";
import { useWorkflowTemplates, useCreateTemplate, type TemplateListItem } from "../hooks/use-workflow-templates";

function slugify(s: string): string {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48);
}

function CreateModal({ kind, onClose, onCreated }: { kind: string; onClose: () => void; onCreated: (code: string) => void }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [subjectType, setSubjectType] = useState("matter");
  const create = useCreateTemplate();
  const effectiveCode = code || slugify(name);
  const submit = async () => {
    if (!effectiveCode) { showToast("A name is required", "error"); return; }
    try {
      await create.mutateAsync({ code: effectiveCode, description: name, subject_type: subjectType, kind });
      onCreated(effectiveCode);
    } catch (e) { showToast((e as Error).message, "error"); }
  };
  return (
    <div role="dialog" aria-modal="true" onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", display: "grid", placeItems: "center", zIndex: 50 }}>
      <div className="wfa" style={{ background: "var(--color-background-primary)", borderRadius: "var(--border-radius-lg)", padding: 20, width: 420, maxWidth: "92vw" }}>
        <h2 style={{ margin: "0 0 12px", fontSize: "var(--text-lg)" }}>New {kind === "block" ? "block" : "workflow"}</h2>
        <div className="wfa-fld"><label>Name</label><input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === "block" ? "e.g. Standard title searches" : "e.g. QLD conveyancing"} /></div>
        <div className="wfa-fld"><label>Code (stable identity)</label><input value={effectiveCode} onChange={(e) => setCode(slugify(e.target.value))} placeholder="auto from name" /></div>
        {kind !== "block" && <div className="wfa-fld"><label>Runs on (anchor type)</label><input value={subjectType} onChange={(e) => setSubjectType(e.target.value)} /></div>}
        <div style={{ display: "flex", gap: 9, justifyContent: "flex-end", marginTop: 8 }}>
          <button className="wfa-btn ghost" onClick={onClose}>Cancel</button>
          <button className="wfa-btn primary" onClick={submit} disabled={create.isPending || !effectiveCode}>{create.isPending ? "Creating…" : "Create draft"}</button>
        </div>
      </div>
    </div>
  );
}

function StatusChips({ t }: { t: TemplateListItem }) {
  return (
    <span style={{ display: "inline-flex", gap: 5 }}>
      {t.activeVersion > 0 && <span className="wfa-status active">active v{t.activeVersion}</span>}
      {t.draftVersion > 0 && <span className="wfa-status draft">draft v{t.draftVersion}</span>}
      {t.activeVersion === 0 && t.draftVersion === 0 && <span className="wfa-status archived">archived</span>}
    </span>
  );
}

export function WorkflowTemplatesPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"workflow" | "block">("workflow");
  const [creating, setCreating] = useState(false);
  const { data, isLoading, isError, error } = useWorkflowTemplates();
  const rows = useMemo(() => (data?.templates ?? []).filter((t) => t.kind === tab), [data, tab]);
  // №16.1.1 — the teaching gate. `canAuthor` is only false once the read resolved;
  // treat undefined (still loading) as "may author" so the button never flickers.
  const canAuthor = data?.canAuthor !== false;

  return (
    <div className="wfa wfa-list">
      <div className="wfa-list__head">
        <h1>Workflows</h1>
        <div className="wfa-spacer" />
        {canAuthor && <button className="wfa-btn primary" onClick={() => setCreating(true)}>+ New {tab === "block" ? "block" : "workflow"}</button>}
      </div>

      {!isLoading && !canAuthor && (
        <div className="wfa-note" role="status" style={{ marginBottom: 12 }}>
          <b>Authoring needs the workflow-author role.</b> You can view the processes your team runs here, but designing or editing a workflow requires the <em>Workflow author</em> role — ask a workspace owner to assign it.
        </div>
      )}

      <div className="wfa-tabs" role="tablist">
        <button role="tab" aria-selected={tab === "workflow"} className={tab === "workflow" ? "on" : ""} onClick={() => setTab("workflow")}>Workflows</button>
        <button role="tab" aria-selected={tab === "block"} className={tab === "block" ? "on" : ""} onClick={() => setTab("block")}>Blocks</button>
      </div>

      {isLoading && <div className="wfa-empty">Loading templates…</div>}
      {isError && <div className="wfa-empty" style={{ color: "var(--color-text-danger)" }}>{(error as Error)?.message ?? "Failed to load"}</div>}
      {!isLoading && !isError && rows.length === 0 && (
        <div className="wfa-empty">No {tab === "block" ? "blocks" : "workflows"} yet.{canAuthor ? " Create one to author it on the canvas." : ""}</div>
      )}

      {rows.map((t) => (
        <button key={t.code} className="wfa-trow" onClick={() => navigate(`/workflows/${encodeURIComponent(t.code)}`)}>
          <div className="wfa-trow__main">
            <div className="wfa-trow__title">
              {t.kind === "block" && <span className="wfa-status block">block</span>}
              {t.description || t.code}
              <StatusChips t={t} />
            </div>
            <div className="wfa-trow__desc">{t.code} · runs on {t.subjectType}{t.source === "derived" ? " · derived" : ""}</div>
          </div>
          <div className="wfa-trow__meta">
            <span>{t.segmentCount} seg · {t.stepCount} steps</span>
            {t.runningInstances > 0 && <span>{t.runningInstances} running</span>}
            <i className="ti ti-chevron-right" aria-hidden="true" />
          </div>
        </button>
      ))}

      {creating && <CreateModal kind={tab} onClose={() => setCreating(false)} onCreated={(code) => { setCreating(false); navigate(`/workflows/${encodeURIComponent(code)}`); }} />}
    </div>
  );
}
